import moment from "moment";
import React, { useState, useEffect, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Activity, RotateCcw } from "lucide-react";
import api from "../../api/api";
import { useTranslation } from "react-i18next";
import { getSensorDepths } from "../../utils/sensorDepths";
import { soilMoistureNumber } from "../common/SoilMoistureLevels";

const SubSoil = ({ codeSensor, sensorDepths }) => {
  const { t, i18n } = useTranslation();
  const depths = useMemo(() => getSensorDepths(sensorDepths), [sensorDepths]);
  const [historyData, setHistoryData] = useState([]);
  const [status, setStatus] = useState("loading");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setHistoryData([]);
    if (!codeSensor) {
      setStatus("empty");
      return () => controller.abort();
    }
    setStatus("loading");
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    api.get(`/sensor/sensor-history/${codeSensor}/${start}/${end}?pageNum=1&limit=28`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const history = Array.isArray(data.history) ? data.history : [];
        setHistoryData(history);
        setStatus(history.length ? "ready" : "empty");
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatus("error");
      });
    return () => controller.abort();
  }, [codeSensor, retry]);

  const chart = useMemo(() => {
    const observedAt = (reading) => reading.timestamp_arduino && reading.timestamp_arduino !== "null"
      ? reading.timestamp_arduino : reading.time;
    const readings = [...historyData].sort((a, b) => moment(observedAt(a)).valueOf() - moment(observedAt(b)).valueOf());
    const colors = ["#FC5566", "#3CB371", "#1E90FF"];
    return {
      labels: readings.map((reading) => {
        const date = moment(observedAt(reading)).locale(i18n.language);
        return [date.format("DD MMM"), date.format("HH:mm")];
      }),
      datasets: ["mv1", "mv2", "mv3"].map((key, index) => ({
        label: `${depths[key]} cm`,
        data: readings.map((reading) => soilMoistureNumber(reading[key])),
        borderColor: colors[index],
        backgroundColor: colors[index],
        borderWidth: 2.5,
        borderDash: [[], [6, 3], [2, 3]][index],
        pointRadius: 1.5,
        pointHoverRadius: 5,
        pointHitRadius: 14,
        tension: 0,
        spanGaps: false,
        fill: false,
      })),
    };
  }, [historyData, depths, i18n.language]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { intersect: false, mode: "index" },
    layout: { padding: { top: 8, right: 8, bottom: 4 } },
    plugins: {
      legend: { position: "bottom", labels: { color: "#526477", usePointStyle: true, pointStyle: "line", boxWidth: 18, padding: 16, font: { size: 11 } } },
      tooltip: {
        padding: 12,
        callbacks: {
          title: (items) => chart.labels[items[0]?.dataIndex]?.join(" ") || "",
          label: (context) => `${context.dataset.label} : ${context.parsed.y === null ? "—" : `${context.parsed.y.toFixed(1)} %`}`,
        },
      },
    },
    scales: {
      y: {
        min: 0, max: 100,
        title: { display: true, text: `${t("Humidity")} (%)`, color: "#526477", font: { size: 11 } },
        ticks: { stepSize: 25, color: "#667783", font: { size: 11 }, callback: (value) => `${value}%` },
        border: { display: false },
        grid: { color: "rgba(102,119,131,.12)", drawTicks: false },
      },
      x: {
        ticks: { autoSkip: true, maxTicksLimit: 4, maxRotation: 0, color: "#667783", padding: 8, font: { size: 10 } },
        border: { display: false },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="field-soil-chart-section">
      <div className="field-soil-chart-caption">
        <span>{t("humidity_percent_full")}</span>
        <span>{t("history")}</span>
      </div>
      <div className="field-soil-chart" aria-busy={status === "loading"}>
        {status === "ready" ? (
          <Line data={chart} options={options} role="img" aria-label={`${t("Humidity")} : ${depths.mv1}, ${depths.mv2}, ${depths.mv3} cm`} />
        ) : (
          <div className={`field-soil-chart-message${status === "loading" ? " is-loading" : ""}`} role="status">
            <Activity size={24} aria-hidden="true" />
            <p>{status === "loading" ? t("loading") : status === "error" ? t("error_fetching_history") : t("no_data", { defaultValue: "No sensor history found for this period." })}</p>
            {status === "error" && <button type="button" onClick={() => setRetry((value) => value + 1)}><RotateCcw size={14} aria-hidden="true" />{t("retry")}</button>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubSoil;
